package org.gymly.ui.admin

import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.decodeToImageBitmap
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminCatalogService
import org.gymly.model.AdminCatalogServiceUpsert
import org.gymly.model.AdminServiceType
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.userMessage
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn

private const val SERVICES_MOBILE_PAGE_SIZE = 10

private enum class ServicesDateFilter(val label: String, val days: Int?) {
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

private sealed class ServicesDialog {
    data object Create : ServicesDialog()
    data class Edit(val service: AdminCatalogService) : ServicesDialog()
    data class Detail(val service: AdminCatalogService) : ServicesDialog()
    data class Delete(val service: AdminCatalogService) : ServicesDialog()
}

@Composable
fun AdminServicesScreen() {
    val scope = rememberCoroutineScope()
    var services by remember { mutableStateOf<List<AdminCatalogService>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(ServicesDateFilter.All) }
    var dialog by remember { mutableStateOf<ServicesDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    val pendingApprovals = LocalPendingApprovalNotifier.current

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                services = AdminRepo.api.listServices()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los servicios.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(services, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        services.filter { s ->
            val matchesQuery = q.isEmpty() ||
                s.name.lowercase().contains(q) ||
                s.type.labelEs().lowercase().contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = s.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = listOf(query, dateFilter),
    )
    val total = filtered.size
    val classes = filtered.count { it.type == AdminServiceType.CLASS }
    val sessions = filtered.count { it.type == AdminServiceType.SESSION }
    val active = filtered.count { it.active }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        // Celular: 10 por página (máximo 20 en escritorio); el tamaño se decide aquí porque depende del ancho.
        val pageSize = if (compact) SERVICES_MOBILE_PAGE_SIZE else ADMIN_LIST_PAGE_SIZE
        val pageItems = remember(filtered, listPaging.sort, listPaging.page, pageSize) {
            filtered.adminSortedPaged(listPaging.sort, listPaging.page, pageSize) { key ->
                when (key) {
                    "type" -> { s -> s.type.labelEs().lowercase() }
                    "duration" -> { s -> s.durationMin }
                    "active" -> { s -> if (s.active) 1 else 0 }
                    else -> { s -> s.name.lowercase() }
                }
            }
        }
        Column(Modifier.fillMaxSize()) {
            ServicesHeader(dateFilter) { dateFilter = it }
            Spacer(Modifier.height(16.dp))
            ServicesKpiRow(total, classes, sessions, active, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            ServicesDirectoryCard(
                services = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = ServicesDialog.Create },
                onView = { dialog = ServicesDialog.Detail(it) },
                onEdit = { dialog = ServicesDialog.Edit(it) },
                onDelete = { dialog = ServicesDialog.Delete(it) },
            )
        }
    }

    when (val d = dialog) {
        is ServicesDialog.Detail -> ServiceDetailDialog(d.service) { dialog = null }
        is ServicesDialog.Create -> ServiceFormDialog(
            title = "Agregar servicio",
            initial = null,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createService(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage("No se pudo guardar el servicio.")
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is ServicesDialog.Edit -> ServiceFormDialog(
            title = "Editar servicio",
            initial = d.service,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateService(d.service.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage("No se pudo guardar el servicio.")
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is ServicesDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar servicio") },
            text = {
                Text("¿Seguro que deseas eliminar «${d.service.name}»? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteService(d.service.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) {
                                dialog = null
                                error = null
                            } else {
                                error = e.userMessage("No se pudo eliminar el servicio.")
                            }
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun ServicesHeader(
    dateFilter: ServicesDateFilter,
    onFilterChange: (ServicesDateFilter) -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Servicios", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Clases y sesiones reservables desde /api/services (nombre, tipo, duración, activo).",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                Text(dateFilter.label, color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                ServicesDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.label) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun ServicesKpiRow(
    total: Int,
    classes: Int,
    sessions: Int,
    active: Int,
    compact: Boolean,
) {
    val items = listOf(
        Triple("Total de servicios", total.toString(), AdminColors.Primary),
        Triple("Clases", classes.toString(), Color(0xFF2E7D32)),
        Triple("Sesiones", sessions.toString(), Color(0xFF7C3AED)),
        Triple("Activos", active.toString(), Color(0xFFF59E0B)),
    )
    if (compact) {
        // Celular: una franja de indicadores de una línea, para dejar el espacio a la lista.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            items.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            items.forEach { (label, value, accent) ->
                ServiceKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun ServiceKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.ServicesDirectoryCard(
    services: List<AdminCatalogService>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminCatalogService) -> Unit,
    onEdit: (AdminCatalogService) -> Unit,
    onDelete: (AdminCatalogService) -> Unit,
) {
    val pageSize = if (compact) SERVICES_MOBILE_PAGE_SIZE else ADMIN_LIST_PAGE_SIZE
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Catálogo de servicios", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                ServiceSearchField(query, onQueryChange)
                if (canWriteModule(AdminModuleAccess.SERVICES)) Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Nuevo servicio", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Catálogo de servicios", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) {
                        ServiceSearchField(query, onQueryChange)
                    }
                    if (canWriteModule(AdminModuleAccess.SERVICES)) Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nuevo servicio", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay servicios todavía.", color = AdminColors.TextMuted)
            }
            // Celular: altura fija para que la lista de servicios siempre tenga espacio y se pueda desplazar.
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    services.forEach { s -> ServiceMobileCard(s, onView, onEdit, onDelete) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                    pageSize = pageSize,
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                ServicesTable(
                    services,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                    pageSize = pageSize,
                )
            }
        }
    }
}

@Composable
private fun ServiceSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun ServicesTable(
    services: List<AdminCatalogService>,
    listPaging: AdminListPaging,
    onView: (AdminCatalogService) -> Unit,
    onEdit: (AdminCatalogService) -> Unit,
    onDelete: (AdminCatalogService) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Servicio", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.6f))
            AdminSortableHeader("Tipo", "type", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
            AdminSortableHeader("Duración", "duration", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminSortableHeader("Activo", "active", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            services.forEach { s ->
                val duration = s.durationMin?.let { "$it min" } ?: "—"
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.6f), verticalAlignment = Alignment.CenterVertically) {
                        if (!s.imageUrl.isNullOrBlank()) {
                            ServiceThumb(url = s.imageUrl, size = 40.dp)
                            Spacer(Modifier.width(10.dp))
                        } else {
                            Box(
                                Modifier
                                    .size(8.dp)
                                    .clip(CircleShape)
                                    .background(Color(s.type.dotColorArgb())),
                            )
                            Spacer(Modifier.width(10.dp))
                        }
                        Text(s.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                    }
                    Text(s.type.labelEs(), Modifier.weight(0.9f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Text(duration, Modifier.weight(0.8f), color = AdminColors.TextMuted, fontSize = 13.sp, maxLines = 1)
                    Box(Modifier.weight(0.8f)) { ServiceActiveBadge(s.active) }
                    Row(Modifier.width(140.dp)) {
                        IconButton(onClick = { onView(s) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        if (canWriteModule(AdminModuleAccess.SERVICES)) IconButton(onClick = { onEdit(s) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.Primary)
                        }
                        if (canDeleteModule(AdminModuleAccess.SERVICES)) IconButton(onClick = { onDelete(s) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun ServiceMobileCard(
    service: AdminCatalogService,
    onView: (AdminCatalogService) -> Unit,
    onEdit: (AdminCatalogService) -> Unit,
    onDelete: (AdminCatalogService) -> Unit,
) {
    val duration = service.durationMin?.let { "$it min" } ?: "—"
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (!service.imageUrl.isNullOrBlank()) {
                ServiceThumb(url = service.imageUrl, size = 48.dp)
                Spacer(Modifier.width(10.dp))
            }
            Column(Modifier.weight(1f)) {
                Text(service.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                Text("${service.type.labelEs()} · $duration", color = AdminColors.TextMuted, fontSize = 12.sp)
            }
        }
        Spacer(Modifier.height(8.dp))
        ServiceActiveBadge(service.active)
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(service) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            if (canWriteModule(AdminModuleAccess.SERVICES)) IconButton(onClick = { onEdit(service) }) {
                Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
            }
            if (canDeleteModule(AdminModuleAccess.SERVICES)) IconButton(onClick = { onDelete(service) }) {
                Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun ServiceThumb(
    url: String?,
    size: Dp,
    modifier: Modifier = Modifier,
    localModel: String? = null,
    fillWidth: Boolean = false,
) {
    val model = localModel ?: url?.takeIf { it.isNotBlank() }
    Box(
        modifier
            .then(if (fillWidth) Modifier.fillMaxWidth().height(size) else Modifier.size(size))
            .clip(RoundedCornerShape(8.dp))
            .background(AdminColors.Outline.copy(alpha = 0.25f)),
        contentAlignment = Alignment.Center,
    ) {
        if (model != null) {
            AsyncImage(
                model = model,
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop,
            )
        }
    }
}

@Composable
private fun ServiceActiveBadge(active: Boolean) {
    val bg = if (active) Color(0xFFE8F5E9) else Color(0xFFF3F4F6)
    val fg = if (active) Color(0xFF2E7D32) else AdminColors.TextMuted
    Text(
        if (active) "Activo" else "Inactivo",
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun ServiceDetailDialog(service: AdminCatalogService, onDismiss: () -> Unit) {
    val duration = service.durationMin?.let { "$it min" } ?: "—"
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("Detalle del servicio", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                Icon(
                    Icons.Outlined.Close,
                    contentDescription = "Cerrar",
                    tint = AdminColors.TextMuted,
                    modifier = Modifier.minimumInteractiveComponentSize().clickable(onClick = onDismiss).size(22.dp),
                )
            }
            Spacer(Modifier.height(16.dp))
            service.imageUrl?.takeIf { it.isNotBlank() }?.let { url ->
                ServiceThumb(url = url, size = 160.dp, fillWidth = true)
                Spacer(Modifier.height(16.dp))
            }
            ServiceDetailRow("Nombre", service.name)
            ServiceDetailRow("Tipo", service.type.labelEs())
            ServiceDetailRow("Duración (minutos)", duration)
            ServiceDetailRow("Descripción", service.description?.takeIf { it.isNotBlank() } ?: "—")
            ServiceDetailRow("Activo", if (service.active) "Activo" else "Inactivo")
            Spacer(Modifier.height(16.dp))
            Button(
                onClick = onDismiss,
                modifier = Modifier.align(Alignment.End),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                ),
            ) {
                Text("Cerrar")
            }
        }
    }
}

@Composable
private fun ServiceDetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun ServiceFormDialog(
    title: String,
    initial: AdminCatalogService?,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminCatalogServiceUpsert) -> Unit,
) {
    var name by remember(initial) { mutableStateOf(initial?.name.orEmpty()) }
    var type by remember(initial) { mutableStateOf(initial?.type ?: AdminServiceType.CLASS) }
    var duration by remember(initial) { mutableStateOf(initial?.durationMin?.toString().orEmpty()) }
    var active by remember(initial) { mutableStateOf(initial?.active ?: true) }
    var description by remember(initial) { mutableStateOf(initial?.description.orEmpty()) }
    var imageName by remember { mutableStateOf<String?>(null) }
    var pickedPayload by remember { mutableStateOf<org.gymly.model.PickedImagePayload?>(null) }
    var formError by remember { mutableStateOf<String?>(null) }
    var typeMenu by remember { mutableStateOf(false) }
    val focusName = remember { FocusRequester() }
    val focusDuration = remember { FocusRequester() }

    LaunchedEffect(Unit) {
        runCatching { focusName.requestFocus() }
    }

    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            ServiceFormLabel("Nombre *")
            ServiceFormField(
                name,
                { name = it },
                "Nombre del servicio",
                focusRequester = focusName,
                onNext = { focusDuration.requestFocus() },
                onPrev = { },
            )
            Spacer(Modifier.height(10.dp))
            ServiceFormLabel("Tipo")
            Box {
                OutlinedButton(onClick = { typeMenu = true }, modifier = Modifier.fillMaxWidth()) {
                    Text(type.labelEs(), color = AdminColors.Text)
                }
                DropdownMenu(expanded = typeMenu, onDismissRequest = { typeMenu = false }) {
                    AdminServiceType.entries.forEach { t ->
                        DropdownMenuItem(
                            text = { Text(t.labelEs()) },
                            onClick = {
                                type = t
                                typeMenu = false
                            },
                        )
                    }
                }
            }
            Spacer(Modifier.height(10.dp))
            ServiceFormLabel("Duración (minutos)")
            ServiceFormField(
                duration,
                { duration = it.filter { ch -> ch.isDigit() } },
                "60",
                focusRequester = focusDuration,
                onNext = { },
                onPrev = { focusName.requestFocus() },
                imeAction = ImeAction.Done,
            )
            Spacer(Modifier.height(10.dp))
            ServiceFormLabel("Descripción")
            OutlinedTextField(
                value = description,
                onValueChange = { description = it },
                modifier = Modifier.fillMaxWidth().heightIn(min = 72.dp),
                placeholder = { Text("Qué incluye y para quién es", color = AdminColors.TextMuted) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            Spacer(Modifier.height(10.dp))
            ServiceFormLabel("Foto de referencia")
            val localBitmap = remember(pickedPayload) {
                pickedPayload?.bytes?.takeIf { it.isNotEmpty() }?.let { bytes ->
                    runCatching { bytes.decodeToImageBitmap() }.getOrNull()
                }
            }
            val remoteUrl = initial?.imageUrl?.takeIf { it.isNotBlank() && localBitmap == null }
            if (localBitmap != null) {
                Image(
                    bitmap = localBitmap,
                    contentDescription = "Foto de referencia",
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(160.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(AdminColors.Outline.copy(alpha = 0.25f)),
                    contentScale = ContentScale.Fit,
                )
                Spacer(Modifier.height(8.dp))
            } else if (remoteUrl != null) {
                ServiceThumb(
                    url = remoteUrl,
                    size = 160.dp,
                    fillWidth = true,
                )
                Spacer(Modifier.height(8.dp))
            }
            OutlinedButton(
                onClick = {
                    org.gymly.util.openImagePicker { picked ->
                        if (picked != null) {
                            pickedPayload = org.gymly.model.PickedImagePayload(
                                picked.bytes,
                                picked.fileName,
                                picked.mimeType,
                            )
                            imageName = picked.fileName
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(imageName ?: if (initial?.imageUrl != null) "Cambiar foto" else "Subir foto", color = AdminColors.Text)
            }
            Spacer(Modifier.height(10.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(checked = active, onCheckedChange = { active = it })
                Text("Activo", color = AdminColors.Text, fontSize = 13.sp)
            }
            formError?.let {
                Text(it, color = AdminColors.Error, fontSize = 12.sp, modifier = Modifier.padding(top = 10.dp))
            }
            Spacer(Modifier.height(18.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val n = name.trim()
                        if (n.isEmpty()) {
                            formError = "El nombre es obligatorio."
                            return@Button
                        }
                        val mins = duration.trim().toIntOrNull()
                        if (mins == null || mins < 1) {
                            formError = "Indica una duración en minutos válida."
                            return@Button
                        }
                        formError = null
                        onSave(
                            AdminCatalogServiceUpsert(
                                name = n,
                                type = type,
                                durationMin = mins,
                                active = active,
                                description = description.trim().ifBlank { null },
                                image = pickedPayload,
                            ),
                        )
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    if (saving) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            color = AdminColors.OnBrand,
                            strokeWidth = 2.dp,
                        )
                    } else {
                        Text("Guardar")
                    }
                }
            }
        }
    }
}

@Composable
private fun ServiceFormLabel(text: String) {
    Text(
        text,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        color = AdminColors.Text,
        modifier = Modifier.padding(bottom = 6.dp),
    )
}

@Composable
private fun ServiceFormField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    focusRequester: FocusRequester? = null,
    onNext: (() -> Unit)? = null,
    onPrev: (() -> Unit)? = null,
    imeAction: ImeAction = ImeAction.Next,
) {
    fun goNext() {
        onNext?.invoke()
    }
    fun goPrev() {
        onPrev?.invoke()
    }
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier
            .fillMaxWidth()
            .then(if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier)
            .onPreviewKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                when (event.key) {
                    Key.Enter, Key.NumPadEnter -> {
                        goNext()
                        true
                    }
                    Key.Tab -> {
                        if (event.isShiftPressed) goPrev() else goNext()
                        true
                    }
                    Key.DirectionDown -> {
                        goNext()
                        true
                    }
                    Key.DirectionUp -> {
                        goPrev()
                        true
                    }
                    else -> false
                }
            },
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        keyboardOptions = KeyboardOptions(imeAction = imeAction),
        keyboardActions = KeyboardActions(
            onNext = { goNext() },
            onDone = { goNext() },
        ),
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
